import { createContext, useMemo, type ReactNode } from "react";
import { Outlet } from "react-router-dom";
import { useBudgetAiRunSync } from "hooks/budget/useBudgetAiRunSync";
import type { ProjectState } from "hooks/useProjects/useProjects.types";

export type BudgetAiSyncContextValue = {
  isBudgetAiRunning: boolean;
  isBudgetRerunning: boolean;
  budgetProject: ProjectState | null | undefined;
};

export const BudgetAiSyncContext = createContext<BudgetAiSyncContextValue>({
  isBudgetAiRunning: false,
  isBudgetRerunning: false,
  budgetProject: undefined,
});

type BudgetAiSyncProviderProps = {
  children?: ReactNode;
};

/**
 * Budget route shell: polls AI run status once and invalidates all budget caches
 * on completion so breakdown / fringes / actuals / tax incentives stay in sync.
 */
export const BudgetAiSyncProvider = ({ children }: BudgetAiSyncProviderProps) => {
  const { currentProject, isBudgetAiRunning, isBudgetRerunning } = useBudgetAiRunSync();

  const value = useMemo(
    () => ({
      isBudgetAiRunning,
      isBudgetRerunning,
      budgetProject: currentProject,
    }),
    [isBudgetAiRunning, isBudgetRerunning, currentProject]
  );

  return (
    <BudgetAiSyncContext.Provider value={value}>
      {children ?? <Outlet />}
    </BudgetAiSyncContext.Provider>
  );
};
